import { Button, Dropdown, message } from 'antd';
import { MenuProps } from 'antd';
import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MenuOutlined } from '@ant-design/icons';
import { useToggle } from 'react-use';

import { ConfirmationModal } from '../../../shared/components/confirmation-modal';
import { Feature } from '../../../shared/models/features';
import { translationNamespace } from '../../constants/translation-resources';
import { useDeleteJobMutation, useUpdateJobMutation } from '../../data/mutations/job-mutations';
import { Job } from '../../models/job';
import { permissionSelector } from '../../../shared/data/store/selectors/auth-selectors';
import { AppResources } from '../../../shared/constants/app-resources';

interface ActionsProps {
  job: Job;
}

export const Actions: FC<ActionsProps> = ({ job }) => {
  const { t } = useTranslation(translationNamespace);
  const deleteMutation = useDeleteJobMutation();
  const updateMutation = useUpdateJobMutation();
  const permissions = useSelector(permissionSelector);
  const [showConfirmDeleteModal, toggleShowConfirmDeleteModal] = useToggle(false);

  const remove = useCallback(() => {
    if (job.id == null) {
      return;
    }

    deleteMutation.mutate(job.id, {
      onSuccess: () => {
        message.success(t('jobs.shared.deleteSuccessMessage'));
        toggleShowConfirmDeleteModal();
      },
    });
  }, [deleteMutation, t, job.id, toggleShowConfirmDeleteModal]);

  const toggleEnabled = useCallback(() => {
    if (job.id == null) {
      return;
    }

    updateMutation.mutate(
      { id: job.id, enabled: !job.enabled },
      {
        onSuccess: () => {
          message.success(t('jobs.shared.updateSuccessMessage'));
        },
      }
    );
  }, [updateMutation, t, job]);

  const items = useMemo<MenuProps['items']>(() => {
    const menuItems: MenuProps['items'] = [];

    if (permissions[AppResources.JOB]['UPDATE']) {
      menuItems.push({
        key: 'edit',
        label: <Link to={`automation/${job.id}`}>{t(`${Feature.SHARED}:edit`)}</Link>,
      });
      menuItems.push({
        key: 'enable',
        onClick: () => toggleEnabled(),
        label: job.enabled ? t('jobs.shared.disable') : t('jobs.shared.enable'),
      });
    }
    if (permissions[AppResources.JOB]['DELETE']) {
      menuItems.push({
        key: 'remove',
        onClick: () => toggleShowConfirmDeleteModal(),
        label: t(`${Feature.SHARED}:remove`),
      });
    }

    return menuItems;
  }, [permissions, job.id, job.enabled, t, toggleEnabled, toggleShowConfirmDeleteModal]);

  return (
    <>
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button type="link" icon={<MenuOutlined />}>
          {t(`${Feature.SHARED}:actions`)}
        </Button>
      </Dropdown>
      <ConfirmationModal
        open={showConfirmDeleteModal}
        title={t(`${Feature.SHARED}:areYouSure`)}
        onCancel={toggleShowConfirmDeleteModal}
        onOk={remove}
        content={t(`${Feature.SHARED}:confirmDeleteMsg`, {
          resourceName: job.name,
          buttonTitle: t(`${Feature.SHARED}:confirm`),
        })}
      />
    </>
  );
};
